// Share helpers for athlete profiles. Everything here produces plain text and
// plain URLs only — never HTML — so nothing unsanitized can reach a share
// sheet, a social intent URL or a meta tag.

import { toExcerpt, toPlainText } from "@/lib/text";
import { isDemoRoute } from "@/lib/demoSafety";

/** Canonical public origin. Share links must always point at the live site. */
export const SITE_ORIGIN = "https://sayitinsports.app";

export const athleteCanonicalUrl = (slug: string): string =>
  `${SITE_ORIGIN}/athlete/${encodeURIComponent(slug)}`;

/** Profile-specific share sentence used across every channel. */
export const athleteShareText = (name: string): string =>
  `Meet ${toPlainText(name)} on Say It In Sports — read her story and back her game.`;

export type ShareContent = {
  name: string;
  url: string;
  title: string;
  text: string;
  /** Plain-text description for metadata: approved tagline, else a bio excerpt. */
  description: string;
  imageUrl?: string;
  imageAlt: string;
};

export function buildShareContent(input: {
  name: string;
  slug: string;
  tagline?: string | null;
  bio?: string | null;
  imageUrl?: string | null;
  /** Absolute URL override for non-athlete-record pages (e.g. the profile demo). */
  url?: string;
}): ShareContent {
  const name = toPlainText(input.name);
  const url = input.url ?? athleteCanonicalUrl(input.slug);
  const demo = input.url ? isDemoRoute(new URL(input.url, SITE_ORIGIN).pathname) : false;
  const text = demo
    ? "Explore the SIIS Athlete Profile Demo. Avery Morgan is fictional; all content is illustrative and demo actions save nothing."
    : athleteShareText(name);
  const tagline = toPlainText(input.tagline);
  const excerpt = toExcerpt(input.bio, 160);
  return {
    name,
    url,
    title: `${name} — Say It In Sports`,
    text,
    description: tagline || excerpt || text,
    imageUrl: input.imageUrl ?? undefined,
    imageAlt: `${name} — athlete profile photo on Say It In Sports`,
  };
}

export type ShareTarget = {
  key: "facebook" | "linkedin" | "x" | "whatsapp" | "email";
  label: string;
  href: string;
};

/** Public, password-free share endpoints. No OAuth, no posting permissions. */
export function shareTargets(content: ShareContent): ShareTarget[] {
  const u = encodeURIComponent(content.url);
  const t = encodeURIComponent(content.text);
  return [
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { key: "x", label: "X", href: `https://x.com/intent/tweet?text=${t}&url=${u}` },
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${content.text} ${content.url}`)}` },
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodeURIComponent(content.title)}&body=${encodeURIComponent(`${content.text}\n\n${content.url}`)}`,
    },
  ];
}

export const INSTAGRAM_URL = "https://www.instagram.com/";

/** Open an external share window without handing the opener to the target. */
export function openShareWindow(href: string) {
  const w = window.open(href, "_blank", "noopener,noreferrer,width=600,height=640");
  if (w) w.opener = null;
}

export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
